/**
 * CB Application Portal — applicant-facing multi-step application form.
 * Route: /apply  (accessible to any logged-in user)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';
import {
  PageHeader, Card, Btn, Alert,
  colors, spacing, typography, radius, shadows,
  inputStyle, textareaStyle, selectStyle,
} from '../design-system';

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft:                    { label: 'Draft',               color: '#64748b', bg: '#f1f5f9' },
  submitted:                { label: 'Submitted',           color: '#0ea5e9', bg: '#e0f2fe' },
  under_review:             { label: 'Under Review',        color: '#8b5cf6', bg: '#ede9fe' },
  additional_info_required: { label: 'Info Required',       color: '#f59e0b', bg: '#fef3c7' },
  assessment_scheduled:     { label: 'Assessment Scheduled',color: '#06b6d4', bg: '#cffafe' },
  approved:                 { label: 'Approved',            color: '#10b981', bg: '#dcfce7' },
  rejected:                 { label: 'Rejected',            color: '#ef4444', bg: '#fee2e2' },
  withdrawn:                { label: 'Withdrawn',           color: '#94a3b8', bg: '#f8fafc' },
};

const STANDARDS = [
  'ISO 9001:2015', 'ISO 14001:2015', 'ISO 45001:2018', 'ISO 27001:2022',
  'ISO 22000:2018', 'ISO 50001:2018', 'ISO 13485:2016', 'ISO 22301:2019',
  'ISO 37001:2016', 'ISO 17025:2017', 'ISO 15189:2022',
];

const STEPS = [
  { key: 'company',   label: 'Company Details' },
  { key: 'scope',     label: 'Scope & Standards' },
  { key: 'documents', label: 'Documents' },
  { key: 'review',    label: 'Review & Submit' },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function FieldLabel({ children, required }) {
  return (
    <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 6 }}>
      {children}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#64748b', bg: '#f1f5f9' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}30`,
      borderRadius: radius.pill,
      fontSize: typography.fontSizes.xs,
      fontWeight: typography.fontWeights.bold,
      padding: '3px 10px',
      letterSpacing: '0.03em',
    }}>
      {cfg.label}
    </span>
  );
}

function StepIndicator({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: spacing.xl }}>
      {STEPS.map((step, i) => {
        const done   = i < current;
        const active = i === current;
        return (
          <React.Fragment key={step.key}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 32, height: 32,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 700,
                background: active ? colors.primary : done ? '#dcfce7' : colors.background,
                color:      active ? '#fff' : done ? '#166534' : colors.textMuted,
                border:     `2px solid ${active ? colors.primary : done ? '#86efac' : colors.border}`,
                transition: 'all 200ms',
              }}>
                {done ? '✓' : i + 1}
              </div>
              <div style={{
                fontSize: '10px', fontWeight: active ? 700 : 500,
                color: active ? colors.textPrimary : colors.textMuted,
                textAlign: 'center', maxWidth: 80,
              }}>
                {step.label}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? '#86efac' : colors.border, margin: '0 4px', marginBottom: 24 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Step 1: Company Details ────────────────────────────────────────────────────

function StepCompany({ form, setForm }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
        <Field label="Legal Company Name" required>
          <input style={inputStyle} value={form.company_legal_name || ''} onChange={e => set('company_legal_name', e.target.value)} />
        </Field>
        <Field label="Trading Name">
          <input style={inputStyle} value={form.trading_name || ''} onChange={e => set('trading_name', e.target.value)} />
        </Field>
      </div>
      <Field label="Registered Address">
        <textarea style={{ ...textareaStyle, minHeight: 60 }} value={form.company_address || ''} onChange={e => set('company_address', e.target.value)} />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
        <Field label="Website">
          <input style={inputStyle} value={form.company_website || ''} onChange={e => set('company_website', e.target.value)} placeholder="https://..." />
        </Field>
        <Field label="Phone">
          <input style={inputStyle} value={form.company_phone || ''} onChange={e => set('company_phone', e.target.value)} />
        </Field>
      </div>
      <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.lg }}>
        <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: spacing.md }}>
          Primary Contact
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <Field label="Full Name" required>
            <input style={inputStyle} value={form.contact_name || ''} onChange={e => set('contact_name', e.target.value)} />
          </Field>
          <Field label="Job Title">
            <input style={inputStyle} value={form.contact_job_title || ''} onChange={e => set('contact_job_title', e.target.value)} />
          </Field>
          <Field label="Email" required>
            <input style={inputStyle} type="email" value={form.contact_email || ''} onChange={e => set('contact_email', e.target.value)} />
          </Field>
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Scope ──────────────────────────────────────────────────────────────

function StepScope({ form, setForm }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const standards = form.standards_applied_for || [];
  const toggleStandard = (std) => {
    setForm(f => ({
      ...f,
      standards_applied_for: standards.includes(std)
        ? standards.filter(s => s !== std)
        : [...standards, std],
    }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <Field label="Standards Applying For" required>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: spacing.sm, marginTop: 4 }}>
          {STANDARDS.map(std => (
            <label key={std} style={{
              display: 'flex', alignItems: 'center', gap: spacing.sm,
              padding: `8px ${spacing.md}`,
              border: `1px solid ${standards.includes(std) ? colors.primary : colors.border}`,
              borderRadius: radius.md,
              cursor: 'pointer',
              background: standards.includes(std) ? '#eff6ff' : colors.surface,
              transition: 'all 120ms',
              fontSize: typography.fontSizes.sm,
              fontWeight: standards.includes(std) ? typography.fontWeights.semibold : typography.fontWeights.medium,
              color: standards.includes(std) ? colors.primary : colors.textPrimary,
            }}>
              <input type="checkbox" checked={standards.includes(std)} onChange={() => toggleStandard(std)} style={{ accentColor: colors.primary }} />
              {std}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Scope Description" required>
        <textarea style={{ ...textareaStyle, minHeight: 80 }} value={form.scope_description || ''} onChange={e => set('scope_description', e.target.value)} placeholder="Describe the activities/sectors you certify clients in…" />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: spacing.md }}>
        <Field label="Geographic Scope">
          <input style={inputStyle} value={form.geographic_scope || ''} onChange={e => set('geographic_scope', e.target.value)} placeholder="e.g. United Kingdom" />
        </Field>
        <Field label="Years in Operation">
          <input style={inputStyle} type="number" min="0" value={form.years_in_operation || ''} onChange={e => set('years_in_operation', e.target.value)} />
        </Field>
        <Field label="Number of Staff">
          <input style={inputStyle} type="number" min="1" value={form.num_staff || ''} onChange={e => set('num_staff', e.target.value)} />
        </Field>
      </div>
      <Field label="Existing Accreditations">
        <textarea style={{ ...textareaStyle, minHeight: 60 }} value={form.existing_accreditations || ''} onChange={e => set('existing_accreditations', e.target.value)} placeholder="List any current accreditations you hold…" />
      </Field>
    </div>
  );
}

// ── Step 3: Documents ──────────────────────────────────────────────────────────

function StepDocuments({ form, setForm }) {
  const docs = form.documents || [];
  const addDoc = () => setForm(f => ({ ...f, documents: [...(f.documents || []), { name: '', url: '' }] }));
  const updateDoc = (i, k, v) => setForm(f => {
    const d = [...(f.documents || [])];
    d[i] = { ...d[i], [k]: v };
    return { ...f, documents: d };
  });
  const removeDoc = (i) => setForm(f => ({ ...f, documents: (f.documents || []).filter((_, idx) => idx !== i) }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: radius.md, padding: spacing.md, fontSize: typography.fontSizes.sm, color: '#1e40af', lineHeight: 1.6 }}>
        <strong>Required documents:</strong> Quality manual, Impartiality policy, Complaints procedure, Internal audit records (last 12 months), Management review minutes, Organisational chart, List of key personnel and CVs.
      </div>
      <div>
        {docs.map((doc, i) => (
          <div key={i} style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.sm, alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 200px' }}>
              {i === 0 && <FieldLabel>Document Name</FieldLabel>}
              <input style={inputStyle} value={doc.name} onChange={e => updateDoc(i, 'name', e.target.value)} placeholder="e.g. Quality Manual" />
            </div>
            <div style={{ flex: 1 }}>
              {i === 0 && <FieldLabel>URL / Link</FieldLabel>}
              <input style={inputStyle} value={doc.url} onChange={e => updateDoc(i, 'url', e.target.value)} placeholder="https://…" />
            </div>
            <div style={{ paddingTop: i === 0 ? 24 : 0 }}>
              <Btn variant="ghost" size="sm" onClick={() => removeDoc(i)}>Remove</Btn>
            </div>
          </div>
        ))}
        <Btn variant="secondary" size="sm" onClick={addDoc}>Add Document</Btn>
      </div>
    </div>
  );
}

// ── Step 4: Review ─────────────────────────────────────────────────────────────

function StepReview({ form }) {
  const standards = form.standards_applied_for || [];
  const docs      = (form.documents || []).filter(d => d.name);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <ReviewSection title="Company Details">
        <ReviewRow label="Legal Name"     value={form.company_legal_name} />
        <ReviewRow label="Trading Name"   value={form.trading_name} />
        <ReviewRow label="Address"        value={form.company_address} />
        <ReviewRow label="Website"        value={form.company_website} />
        <ReviewRow label="Contact"        value={`${form.contact_name || ''} — ${form.contact_email || ''}`} />
      </ReviewSection>
      <ReviewSection title="Scope & Standards">
        <ReviewRow label="Standards"       value={standards.join(', ') || 'None selected'} />
        <ReviewRow label="Scope"           value={form.scope_description} />
        <ReviewRow label="Geography"       value={form.geographic_scope} />
        <ReviewRow label="Years operating" value={form.years_in_operation} />
        <ReviewRow label="Staff count"     value={form.num_staff} />
      </ReviewSection>
      <ReviewSection title="Documents">
        {docs.length
          ? docs.map((d, i) => <ReviewRow key={i} label={d.name} value={d.url} />)
          : <span style={{ color: colors.textMuted, fontSize: typography.fontSizes.sm }}>No documents added.</span>
        }
      </ReviewSection>
      <div style={{ background: '#fef3c7', border: '1px solid #fde047', borderRadius: radius.md, padding: spacing.md, fontSize: typography.fontSizes.sm, color: '#92400e', lineHeight: 1.6 }}>
        By submitting this application you confirm that all information provided is accurate and complete. False information may result in rejection or revocation of accreditation.
      </div>
    </div>
  );
}

function ReviewSection({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: spacing.sm }}>{title}</div>
      <div style={{ background: colors.background, borderRadius: radius.md, padding: spacing.md, display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: spacing.md, fontSize: typography.fontSizes.sm }}>
      <span style={{ color: colors.textMuted, minWidth: 130, flexShrink: 0 }}>{label}</span>
      <span style={{ color: colors.textPrimary, fontWeight: typography.fontWeights.medium, wordBreak: 'break-word' }}>{value || <span style={{ color: colors.textMuted }}>—</span>}</span>
    </div>
  );
}

// ── Application List ───────────────────────────────────────────────────────────

function ApplicationCard({ app, onSelect }) {
  const cfg = STATUS_CONFIG[app.status] || { label: app.status, color: '#64748b' };
  return (
    <div
      onClick={() => onSelect(app)}
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderLeft: `3px solid ${cfg.color}`,
        borderRadius: radius.md,
        padding: spacing.lg,
        cursor: 'pointer',
        transition: 'box-shadow 150ms',
        marginBottom: spacing.md,
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = shadows.md}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: typography.fontSizes.sm, color: colors.textMuted, marginBottom: 4 }}>{app.reference_number}</div>
          <div style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.bold, color: colors.textPrimary }}>{app.company_legal_name}</div>
          <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, marginTop: 2 }}>
            {(app.standards_applied_for || []).join(' · ')}
          </div>
        </div>
        <StatusBadge status={app.status} />
      </div>
      {app.submitted_at && (
        <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: spacing.sm }}>
          Submitted {new Date(app.submitted_at).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

function MessageThread({ messages, onSend }) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    await onSend(body);
    setBody('');
    setSending(false);
  };
  return (
    <div>
      <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: spacing.md }}>
        {(!messages || !messages.length) && (
          <div style={{ color: colors.textMuted, fontSize: typography.fontSizes.sm }}>No messages yet.</div>
        )}
        {(messages || []).map(m => (
          <div key={m.id} style={{ marginBottom: spacing.md, padding: spacing.sm, background: colors.background, borderRadius: radius.md }}>
            <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, color: colors.textMuted, marginBottom: 4 }}>
              {m.author_name} · {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
            </div>
            <div style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary, lineHeight: 1.5 }}>{m.body}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: spacing.sm }}>
        <input style={{ ...inputStyle, flex: 1 }} value={body} onChange={e => setBody(e.target.value)} placeholder="Add a message…" onKeyDown={e => e.key === 'Enter' && send()} />
        <Btn variant="primary" size="sm" onClick={send} loading={sending} disabled={!body.trim()}>Send</Btn>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function CBApplicationPortal() {
  const [view,         setView]        = useState('list'); // 'list' | 'new' | 'detail'
  const [applications, setApplications] = useState([]);
  const [selected,     setSelected]    = useState(null);
  const [loading,      setLoading]     = useState(true);
  const [step,         setStep]        = useState(0);
  const [form,         setForm]        = useState({});
  const [appId,        setAppId]       = useState(null);
  const [saving,       setSaving]      = useState(false);
  const [msg,          setMsg]         = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get('/cb-applications/my');
      setApplications(res.data);
    } catch {
      flash('Could not load applications.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const startNew = () => { setForm({}); setStep(0); setAppId(null); setView('new'); };

  const saveProgress = async () => {
    setSaving(true);
    try {
      if (appId) {
        await axiosClient.patch(`/cb-applications/my/${appId}`, form);
      } else {
        const res = await axiosClient.post('/cb-applications/', form);
        setAppId(res.data.id);
      }
    } catch {
      flash('Auto-save failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const nextStep = async () => {
    await saveProgress();
    if (step < STEPS.length - 1) setStep(s => s + 1);
  };

  const prevStep = () => { if (step > 0) setStep(s => s - 1); };

  const submit = async () => {
    setSaving(true);
    try {
      let id = appId;
      if (!id) {
        const res = await axiosClient.post('/cb-applications/', form);
        id = res.data.id;
        setAppId(id);
      } else {
        await axiosClient.patch(`/cb-applications/my/${id}`, form);
      }
      await axiosClient.post(`/cb-applications/my/${id}/submit`);
      flash('Application submitted successfully.');
      await loadList();
      setView('list');
    } catch (err) {
      flash(err?.response?.data?.detail || 'Submission failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (app) => {
    try {
      const res = await axiosClient.get(`/cb-applications/my/${app.id}`);
      setSelected(res.data);
      setView('detail');
    } catch {
      flash('Could not load application.', 'error');
    }
  };

  const sendMessage = async (body) => {
    if (!selected) return;
    await axiosClient.post(`/cb-applications/my/${selected.id}/messages`, { body });
    const res = await axiosClient.get(`/cb-applications/my/${selected.id}`);
    setSelected(res.data);
  };

  const canEdit = selected && ['draft', 'additional_info_required'].includes(selected.status);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: spacing.lg }}>
      {view === 'list' && (
        <>
          <PageHeader
            title="Accreditation Applications"
            subtitle="Apply for certification body accreditation or track an existing application"
            actions={<Btn variant="primary" size="md" onClick={startNew}>New Application</Btn>}
          />
          {msg && <Alert type={msg.type} message={msg.text} />}
          {loading ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: spacing.xl }}>Loading…</div>
          ) : applications.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: spacing.xl }}>
              <div style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.bold, color: colors.textPrimary, marginBottom: spacing.sm }}>
                No applications yet
              </div>
              <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, marginBottom: spacing.lg }}>
                Start your accreditation journey by submitting a new application.
              </div>
              <Btn variant="primary" size="md" onClick={startNew}>Start Application</Btn>
            </Card>
          ) : (
            applications.map(app => <ApplicationCard key={app.id} app={app} onSelect={openDetail} />)
          )}
        </>
      )}

      {view === 'new' && (
        <>
          <div style={{ marginBottom: spacing.lg }}>
            <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: typography.fontSizes.sm, padding: 0, marginBottom: spacing.sm }}>
              ← Back to applications
            </button>
            <div style={{ fontSize: typography.fontSizes.xxl, fontWeight: typography.fontWeights.bold, color: colors.textPrimary }}>
              New Accreditation Application
            </div>
          </div>

          {msg && <Alert type={msg.type} message={msg.text} />}
          <StepIndicator current={step} />

          <Card>
            {step === 0 && <StepCompany form={form} setForm={setForm} />}
            {step === 1 && <StepScope   form={form} setForm={setForm} />}
            {step === 2 && <StepDocuments form={form} setForm={setForm} />}
            {step === 3 && <StepReview   form={form} />}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: spacing.xl, paddingTop: spacing.lg, borderTop: `1px solid ${colors.border}` }}>
              <Btn variant="secondary" size="md" onClick={prevStep} disabled={step === 0}>Previous</Btn>
              {step < STEPS.length - 1
                ? <Btn variant="primary" size="md" onClick={nextStep} loading={saving}>Save & Continue</Btn>
                : <Btn variant="success"  size="md" onClick={submit}   loading={saving}>Submit Application</Btn>
              }
            </div>
          </Card>
        </>
      )}

      {view === 'detail' && selected && (
        <>
          <div style={{ marginBottom: spacing.lg }}>
            <button onClick={() => { setView('list'); loadList(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: typography.fontSizes.sm, padding: 0, marginBottom: spacing.sm }}>
              ← Back to applications
            </button>
          </div>
          {msg && <Alert type={msg.type} message={msg.text} />}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.lg }}>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: typography.fontSizes.sm, color: colors.textMuted, marginBottom: 4 }}>{selected.reference_number}</div>
              <div style={{ fontSize: typography.fontSizes.xl, fontWeight: typography.fontWeights.bold, color: colors.textPrimary }}>{selected.company_legal_name}</div>
            </div>
            <StatusBadge status={selected.status} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
            <Card>
              <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: spacing.md }}>Application Details</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, fontSize: typography.fontSizes.sm }}>
                {[
                  ['Standards', (selected.standards_applied_for || []).join(', ')],
                  ['Scope',     selected.scope_description],
                  ['Geography', selected.geographic_scope],
                  ['Contact',   selected.contact_name],
                  ['Email',     selected.contact_email],
                  ['Submitted', selected.submitted_at ? new Date(selected.submitted_at).toLocaleDateString() : '—'],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', gap: spacing.sm }}>
                    <span style={{ color: colors.textMuted, minWidth: 80 }}>{l}</span>
                    <span style={{ color: colors.textPrimary, fontWeight: typography.fontWeights.medium }}>{v || '—'}</span>
                  </div>
                ))}
              </div>
              {selected.decision_notes && (
                <div style={{ marginTop: spacing.md, padding: spacing.sm, background: colors.background, borderRadius: radius.sm, fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>
                  <span style={{ fontWeight: 700 }}>Reviewer note: </span>{selected.decision_notes}
                </div>
              )}
            </Card>

            <Card>
              <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: spacing.md }}>Communication</div>
              <MessageThread messages={selected.messages} onSend={sendMessage} />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
